import { useCallback, useState } from 'react';
import { AlertCircle } from 'lucide-react';
import { useClaimHandleMutation } from './api';
import { InviteLinkNote } from '../inviteLinks/InviteLinkNote';
import { BrandMark } from '../onboarding/BrandMark';
import { handleFromInput, MAIL_DOMAIN } from '../onboarding/share';
import { track } from '../onboarding/track';

/**
 * "What's your toiyp?" — the handle is also an address (the mail gateway
 * routes `handle@toiyp.me` into the app), so it is shown as one while typed.
 * The avatar and the rest of the profile come in the first-run tour after.
 */
export function HandleScreen({ inviteLink = null }: { inviteLink?: string | null }) {
  const [handle, setHandle] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [claim, { isLoading }] = useClaimHandleMutation();
  const preview = handleFromInput(handle);

  const onSubmit = useCallback(async () => {
    setError(null);
    try {
      await claim({ handle }).unwrap();
      track({ event: 'handle_claimed' });
    } catch (e) {
      const err = e as { data?: { message?: string | string[] }; status?: unknown };
      if (Array.isArray(err.data?.message)) setError(err.data!.message[0]!);
      else setError(err.data?.message ?? 'That handle is not available');
    }
  }, [claim, handle]);

  return (
    <main className="auth-page">
      <BrandMark />

      <div className="auth-card">
        {inviteLink && <InviteLinkNote token={inviteLink} signedIn />}
        <form
          className="auth-form"
          onSubmit={(e) => {
            e.preventDefault();
            void onSubmit();
          }}
        >
          <div className="field">
            <label className="field__label handle-claim__label" htmlFor="handle-input">
              What&apos;s your toiyp?
            </label>
            <p className="handle-claim__preview" aria-live="polite">
              <span className={`toiyp-address${preview ? '' : ' toiyp-address--empty'}`}>
                {preview || 'you'}@{MAIL_DOMAIN}
              </span>
            </p>
            <input
              id="handle-input"
              className="field__input"
              value={handle}
              onChange={(e) => setHandle(e.target.value)}
              placeholder="grace"
              autoComplete="off"
              autoCapitalize="none"
              spellCheck={false}
              aria-describedby="handle-hint"
            />
            <p className="field__hint" id="handle-hint">
              This is your address. Give it out instead of your phone number. People can write to it
              from the app, or even send an email to it.
            </p>
            <p className="field__hint handle-claim__rules">
              3–30 characters: letters, numbers, dot, dash or underscore.
            </p>
          </div>

          {error && (
            <p className="form-error" role="alert">
              <AlertCircle size={14} aria-hidden="true" />
              {error}
            </p>
          )}

          <button
            type="submit"
            className="form-submit"
            disabled={isLoading || handle.trim().length < 3}
          >
            {isLoading ? '…' : 'Claim my toiyp'}
          </button>
        </form>
      </div>
    </main>
  );
}
